import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, take } from 'rxjs';

import { DynamicTicketTypeNotifyAssigneeMode } from '@app/services/dynamic-ticket-type.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  CompletedId,
  CustomNotificationFieldType,
  CustomNotificationsDialogComponent,
  UntilCompletedOption,
} from '@main-application/inspections/components/inspection-add-dialog/custom-notifications-dialog/custom-notifications-dialog.component';
import { CustomRemindDialogComponent } from '@main-application/inspections/components/inspection-add-dialog/custom-remind-dialog/custom-remind-dialog.component';
import {
  CustomNotifyId,
  DontNotifyId,
  DontNotifyOption,
  OnDueDateNotifyId,
} from '@main-application/inspections/components/inspection-add-dialog/inspection-add-regular-form/inspection-add-regular-form.component';
import { GetInspectionRemindListConst } from '@main-application/inspections/models/inspection-notify-list.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { isExist } from '@shared/utils/exist.util';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

import {
  CREATION_DATE_ID,
  CustomNotificationDialogResponse,
  TicketCustomNotificationFieldsDialogComponent,
} from '../ticket-custom-notification-fields-dialog/ticket-custom-notification-fields-dialog.component';

@Component({
  selector: 'app-ticket-custom-notification-dialog',
  standalone: true,
  imports: [
    CommonModule,
    RadioButtonDropdownModule,
    MatButtonModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './ticket-custom-notification-dialog.component.html',
  styleUrls: ['./ticket-custom-notification-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketCustomNotificationDialogComponent extends PuSubscribable implements OnInit {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public dialog = inject(MatDialog);
  public dialogRef: MatDialogRef<CustomNotificationsDialogComponent> = inject(MatDialogRef);
  private formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);

  public data: {
    dueDate: Date;
    // notifyAssignee?: boolean;
    customNotifyOnMode?: DynamicTicketTypeNotifyAssigneeMode;
    notifyOn?: number;
    // remindOn?: number;
    thenRemindOn?: number;
    until?: number;
    notifyLabel?: string;
  } = inject(MAT_DIALOG_DATA);

  form = this.formBuilder.group({
    // notifyAssignee: [this.data.notifyAssignee],
    customNotifyOnMode: [DynamicTicketTypeNotifyAssigneeMode.OnDueDate],
    notifyOn: [OnDueDateNotifyId],
    remindOn: [DontNotifyId],
    thenRemindOn: [DontNotifyId],
    until: [CompletedId],
  });

  notifyOptions = this.createNotifyOptions('notifyOn', GetInspectionRemindListConst('remindOn'), true);
  // remindOptions = this.createNotifyOptions('remindOn', GetInspectionRemindListConst('remindOn'), true);
  remindThenOptions = this.createNotifyOptions('thenRemindOn', GetInspectionRemindListConst('thenRemindOn'), true);
  untilOptions = this.createNotifyOptions('until', GetInspectionRemindListConst('until'), true);

  DontNotifyId = DontNotifyId;
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  orderError = false;

  get thenRemindOn(): UntypedFormControl {
    return this.form.get('thenRemindOn') as UntypedFormControl;
  }

  // get notifyAssignee(): UntypedFormControl {
  //   return this.form.get('notifyAssignee') as UntypedFormControl;
  // }

  ngOnInit(): void {
    this.initForm();
    this.initFormChanges();
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (!this.form.valid) return;
    if (this.form.get('customNotifyOnMode').value === DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation) {
      const days = this.form.get('notifyOn').value / CREATION_DATE_ID;
      this.form.get('notifyOn').setValue(days, { emitEvent: false });
    }

    this.dialogRef.close(this.form.value);
  }

  private openCustomDateDialog(fieldType: CustomNotificationFieldType = 'notifyOn', prevValue?: number) {
    this.dialog
      .open(CustomRemindDialogComponent, {
        panelClass: 'modal-container-without-padding',
        width: '424px',
        autoFocus: false,
        data: {
          fieldType,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res === undefined) {
          this.form.get(fieldType).setValue(prevValue ?? DontNotifyId);
          return;
        }

        const days = res.days;
        const options = this.getOptions(fieldType);
        const option = options.find(o => o.value === days);
        if (!option) {
          const newOption = this.createNewOption(fieldType, days);
          this.form.get(fieldType).setValue(newOption.value);
        } else {
          this.form.get(fieldType).setValue(option.value);
        }

        // if (fieldType === 'notifyOn' || fieldType === 'remindOn') {
        //   this.checkNotifyOrder();
        // }

        // if (fieldType === 'remindOn' && this.form.value.notifyAssignee === false) {
        //   this.form.get('notifyAssignee').setValue(true);
        // }
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  private openCustomNotifyOnDialog(prevValue?: number) {
    this.dialog
      .open(TicketCustomNotificationFieldsDialogComponent, {
        panelClass: 'modal-container-without-padding',
        width: '464px',
        autoFocus: false,
        data: {
          fieldType: 'notifyOn',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((res?: CustomNotificationDialogResponse) => {
        if (res === undefined) {
          this.form.get('notifyOn').setValue(prevValue ?? DontNotifyId);
          return;
        }

        if (res.isCreationDate) {
          this.form.get('customNotifyOnMode').setValue(DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation);
        }

        const days = res.days;
        const options = this.getOptions('notifyOn');
        const newOption = this.createNewOption('notifyOn', days, res.isCreationDate ?? false);
        const option = options.find(o => o.value === days && o.label === newOption.label);
        if (!option) {
          this.form.get('notifyOn').setValue(newOption.value);
        } else {
          this.form.get('notifyOn').setValue(option.value);
        }
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  private createNewOption(
    fieldType: CustomNotificationFieldType,
    value: number,
    isCreationDate = false
  ): IRadioButtonOption<number> {
    const label = this.getOptionLabel(fieldType, value, isCreationDate);
    const newOption = {
      value: value,
      label,
      groupValue: isCreationDate ? 'creationDate' : 'dueDate',
      sort: isCreationDate ? value * 100 : value,
    };
    const options = this.getOptions(fieldType);
    options.push(newOption);
    this.updateOptions(fieldType, options);
    return newOption;
  }

  private getOptionLabel(fieldType: CustomNotificationFieldType, value: number, isCreationDate?: boolean): string {
    const days = isCreationDate ? value / CREATION_DATE_ID : value;
    if (fieldType === 'until') {
      return days === 1 ? '1 time' : `${value} times`;
    }
    if (fieldType === 'thenRemindOn') {
      return `Every ${days}d`;
    }
    const baseLabel = isCreationDate ? 'ticket creation' : 'due date';
    return days === 0
      ? `on ${baseLabel}`
      : days < 0
      ? `${Math.abs(value)}d before ${baseLabel}`
      : `${days}d after ${baseLabel}`;
  }

  private getOptions(fieldType: CustomNotificationFieldType): IRadioButtonOption<number>[] {
    switch (fieldType) {
      case 'notifyOn':
        return this.notifyOptions.flat().filter(option => option.value !== CustomNotifyId);
      // case 'remindOn':
      //   return [...this.remindOptions[1]];
      case 'thenRemindOn':
        return [...this.remindThenOptions[1]];
      case 'until':
        return [...this.untilOptions[1]];
    }
    return [];
  }

  updateOptions(fieldType: CustomNotificationFieldType, newOptions: IRadioButtonOption<number>[]) {
    switch (fieldType) {
      case 'notifyOn':
        this.notifyOptions = this.createNotifyOptions(
          'notifyOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
      // case 'remindOn':
      //   this.remindOptions = this.createNotifyOptions(
      //     'remindOn',
      //     newOptions.sort((a, b) => +a.sort - +b.sort)
      //   );
      //   break;
      case 'thenRemindOn':
        this.remindThenOptions = this.createNotifyOptions(
          'thenRemindOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
      case 'until':
        this.untilOptions = this.createNotifyOptions(
          'until',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
    }
  }

  private createNotifyOptions(
    fieldType: CustomNotificationFieldType,
    notifyOptions: IRadioButtonOption<number>[],
    addEmptyOption = false
  ): IRadioButtonOption<number>[][] {
    switch (fieldType) {
      case 'notifyOn':
        const customNotifyOn = this.getCustomOption();
        const options = notifyOptions.map(opt => ({ ...opt, groupValue: opt.groupValue ? opt.groupValue : 'dueDate' }));
        if (addEmptyOption) {
          return [[customNotifyOn], [DontNotifyOption, ...options]];
        }
        return [[customNotifyOn], options];

      // case 'remindOn':
      //   const customRemindOn = this.getCustomOption();
      //   if (addEmptyOption) {
      //     return [[customRemindOn], [{ ...DontNotifyOption, label: "Don't remind" }, ...notifyOptions]];
      //   }
      //   return [[customRemindOn], notifyOptions];

      case 'thenRemindOn':
        const customThenRemind = this.getCustomOption('Set custom frequency');
        if (addEmptyOption) {
          return [[customThenRemind], [{ ...DontNotifyOption, label: "Don't remind" }, ...notifyOptions]];
        }
        return [[customThenRemind], notifyOptions];

      case 'until':
        const customUntil = this.getCustomOption('Set custom');
        if (addEmptyOption) {
          return [[customUntil], [UntilCompletedOption, ...notifyOptions]];
        }
        return [[customUntil], notifyOptions];
    }
  }

  getCustomOption(label = 'Set custom timing') {
    return {
      value: CustomNotifyId,
      label,
      sort: 0,
    };
  }

  private initForm() {
    const { customNotifyOnMode, notifyOn, thenRemindOn, until } = this.data;
    const isCreationDate = customNotifyOnMode === DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation;
    const days = isCreationDate ? notifyOn * CREATION_DATE_ID : notifyOn;
    if (isExist(notifyOn) && (!this.getOptions('notifyOn').find(o => o.value === notifyOn) || isCreationDate)) {
      this.createNewOption('notifyOn', days, isCreationDate);
    }
    // if (isExist(remindOn) && !this.remindOptions[1].find(o => o.value === remindOn)) {
    //   this.createNewOption('remindOn', remindOn);
    // }
    if (thenRemindOn > 0 && !this.remindThenOptions[1].find(o => o.value === thenRemindOn)) {
      this.createNewOption('thenRemindOn', thenRemindOn);
    }
    if (until > 0 && !this.untilOptions[1].find(o => o.value === until)) {
      this.createNewOption('until', until);
    }
    this.form = this.formBuilder.group({
      // notifyAssignee: [this.data.notifyAssignee],
      customNotifyOnMode: [customNotifyOnMode ?? DynamicTicketTypeNotifyAssigneeMode.OnDueDate],
      notifyOn: [days ?? OnDueDateNotifyId],
      remindOn: [DontNotifyId],
      thenRemindOn: [thenRemindOn ?? DontNotifyId],
      until: [until ?? CompletedId],
    });
  }

  private initFormChanges() {
    let prevNotifyOnValue = this.form.controls.notifyOn.value;
    this.form.controls.notifyOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(notifyOn => {
        if (notifyOn === CustomNotifyId) {
          this.openCustomNotifyOnDialog(prevNotifyOnValue);
        }
        prevNotifyOnValue = notifyOn;

        if (notifyOn === DontNotifyId) {
          // this.form.controls.notifyAssignee.setValue(false);
          // this.form.controls.remindOn.setValue(DontNotifyId);
          this.form.controls.customNotifyOnMode.setValue(DynamicTicketTypeNotifyAssigneeMode.OnDueDate);
          this.form.controls.thenRemindOn.setValue(DontNotifyId);
          this.form.controls.until.setValue(CompletedId);
        }

        const selectedOption = this.getOptions('notifyOn').find(o => o.value === notifyOn);
        if (selectedOption?.groupValue === 'dueDate') {
          this.form.controls.customNotifyOnMode.setValue(DynamicTicketTypeNotifyAssigneeMode.OnDueDate);
        } else if (selectedOption?.groupValue === 'creationDate') {
          this.form.controls.customNotifyOnMode.setValue(DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation);
        }

        // this.checkNotifyOrder();
      })
      .untilDestroyed(this);

    // let prevRemindOnValue = this.form.controls.remindOn.value;
    // this.form.controls.remindOn.valueChanges
    //   .pipe(distinctUntilChanged())
    //   .subscribe(remindOn => {
    //     if (remindOn === CustomNotifyId) {
    //       this.openCustomDateDialog('remindOn', prevRemindOnValue);
    //     }
    //     prevRemindOnValue = remindOn;

    //     if (remindOn !== DontNotifyId && remindOn !== CustomNotifyId && this.form.value.notifyOn === DontNotifyId) {
    //       this.form.controls.notifyOn.setValue(OnDueDateNotifyId);
    //     } else if (remindOn === DontNotifyId && this.form.value.thenRemindOn !== DontNotifyId) {
    //       this.form.controls.thenRemindOn.setValue(DontNotifyId);
    //     }

    //     if (remindOn !== DontNotifyId && this.form.value.notifyAssignee === false) {
    //       this.form.controls.notifyAssignee.setValue(true);
    //     }

    //     this.checkNotifyOrder();
    //   })
    //   .untilDestroyed(this);

    let prevThenRemindOnValue = this.form.controls.thenRemindOn.value;
    this.form.controls.thenRemindOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(thenRemindOn => {
        if (thenRemindOn === CustomNotifyId) {
          this.openCustomDateDialog('thenRemindOn', prevThenRemindOnValue);
        }
        prevThenRemindOnValue = thenRemindOn;

        // if (
        //   thenRemindOn !== DontNotifyId &&
        //   thenRemindOn !== CustomNotifyId &&
        //   this.form.value.remindOn === DontNotifyId
        // ) {
        //   const options = this.getOptions('remindOn');
        //   const option = options.find(o => o.value === thenRemindOn);
        //   if (!option) {
        //     const newOption = this.createNewOption('remindOn', thenRemindOn);
        //     this.form.get('remindOn').setValue(newOption.value);
        //   } else {
        //     this.form.get('remindOn').setValue(option.value);
        //   }
        //   this.form.controls.notifyAssignee.setValue(true);
        // }

        if (thenRemindOn === DontNotifyId && this.form.value.until !== CompletedId) {
          this.form.controls.until.setValue(CompletedId);
        }
      })
      .untilDestroyed(this);

    let prevUntilValue = this.form.controls.until.value;
    this.form.controls.until.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(until => {
        if (until === CustomNotifyId) {
          this.openCustomDateDialog('until', prevUntilValue);
        }
        prevUntilValue = until;
      })
      .untilDestroyed(this);

    // this.form.controls.notifyAssignee.valueChanges
    //   .pipe(distinctUntilChanged())
    //   .subscribe(notifyAssignee => {
    //     if (notifyAssignee === false) {
    //       this.form.controls.remindOn.setValue(DontNotifyId);
    //       this.form.controls.thenRemindOn.setValue(DontNotifyId);
    //       this.form.controls.until.setValue(CompletedId);
    //     }
    //   })
    //   .untilDestroyed(this);
  }
}
